<script setup>
import { computed } from 'vue'
import ShowService from './../services/show-service'
import modalService from '@/services/modal-service'
import { useStore } from '@composables/use-store'
import { useQueueService } from '@composables/use-queue-service'

const props = defineProps({
    resource: {
        type: [Object, Array],
        default: () => {},
    },
    icon: {
        type: Boolean,
        default: false,
    },
    /**
     * Use circular icon button style (new design)
     */
    circular: {
        type: Boolean,
        default: false,
    },
    /**
     * Size of the circular button: 'small' (40px), 'medium' (56px), 'large' (65px)
     */
    size: {
        type: String,
        default: 'medium',
        validator: value => ['small', 'medium', 'large'].includes(value),
    },
    type: {
        type: String,
        default: 'primary', // tag, primary, primaryFlat, secondary, transparent, tagSecondary, facebook, twitter, highlight, tagBlack, black, nadir, blue, follow
    },
    playlist: {
        type: Object,
        default: () => {},
    },
})

const store = useStore()
const queueService = useQueueService()

const playback = computed(() => store.state.playback)

const loggedIn = computed(() => store.getters['auth/loggedIn'])

const playing = computed(
    () =>
        (playback.value.status === 'playing' || playback.value.status === 'loading') &&
        ((playback.value.resource &&
            props.resource &&
            playback.value.resource.type === props.resource.type &&
            playback.value.resource.id === props.resource.id) ||
            (props.playlist && store.state.queue.playlistId === props.playlist.id))
)

const currentIcon = computed(() => {
    if (props.icon) {
        return playing.value ? 'pause' : 'play_arrow'
    }
    return ''
})

const circularIcon = computed(() => (playing.value ? 'pause-circle' : 'play-circle'))

const isMixLoaded = computed(
    () => store.state.playback.resource && props.resource.id === store.state.playback.resource.id
)

const buttonTitle = computed(() => (playing.value ? 'Pause' : 'Play'))

function increaseReach(id) {
    // const id = this.resource.id

    ShowService().increaseSessionReach(id)
    store.dispatch('shows/increaseReach')
}

function handleClick() {
    if (props.playlist) return

    if (isMixLoaded.value) return queueService.pause()

    if (playing.value) {
        queueService.pause()
    } else if (props.resource && props.resource.type && props.resource.type === 'channel') {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService.playRadioChannel(props.resource.id).catch(e => {
            store.dispatch('appearance/handleNotification', { message: e.message, type: 'warning' })
        })
    } else {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService
            .playMix(props.resource)
            .then(data => {
                increaseReach(props.resource.show_id.id)
            })
            .catch(e => {
                store.dispatch('appearance/handleNotification', { message: e.message, type: 'warning' })
            })
    }
}
</script>

<template>
    <button
        v-if="circular"
        :class="[
            'circular-button',
            size,
            (resource && resource.id) || (playlist && playlist.id) ? '' : 'inactive',
        ]"
        :title="buttonTitle"
        :aria-label="buttonTitle"
        @click="handleClick"
    >
        <BaseIcon
            :name="circularIcon"
            class="circular-icon"
        />
    </button>
    <BaseButton
        v-else
        :icon="currentIcon"
        :button-type="type"
        :class="[(resource && resource.id) || (playlist && playlist.id) ? '' : 'inactive']"
        @click="handleClick"
    >
        <slot>
            <span :title="buttonTitle">
                {{ buttonTitle }}
            </span>
        </slot>
    </BaseButton>
</template>

<style lang="scss" scoped>
@import '@design';

.inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}

.circular-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $c-razzmatazz;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;
    transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;

    &:hover {
        transform: scale(1.05);
    }

    &:active {
        transform: scale(0.95);
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid $c-razzmatazz;
        outline-offset: 2px;
    }
}

.circular-icon {
    display: block;
    width: 100%;
    height: 100%;
}

// Size variants
.small {
    width: 40px;
    height: 40px;
}

.medium {
    width: 56px;
    height: 56px;
}

.large {
    width: 65px;
    height: 65px;
}
</style>
